import { Copy, FilePdf, FileDoc, CircleNotch, ArrowsClockwise, DownloadSimple, ClockCounterClockwise, ArrowSquareOut, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import LockNotice from "@/components/LockNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The export action bar (synthesize / copy / recent / DOCX / PDF) plus the green
// "file ready" delivery bar. All state lives in the parent Exporter; this is the chrome.
export default function ExportToolbar({
  plan, lockReason, synth, pdfBusy, docxBusy, notesEnabled, setNotesEnabled,
  onSynthesize, onCopyMarkdown, onDownloadDocx, onDownloadPdf,
  showRecent, setShowRecent, recent, loadRecent, recentRef,
  pdfLink, docxLink, onDismissReady,
}) {
  const locked = !!lockReason;
  return (
    <>
      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Your Plan, Written Up · Print Preview</span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-600 cursor-pointer select-none mr-1"
            data-testid="notes-toggle-label" title="Add ruled note lines to each checklist page">
            <button type="button" role="switch" aria-checked={notesEnabled}
              onClick={() => setNotesEnabled((v) => !v)} data-testid="notes-toggle"
              className={`relative w-9 h-5 rounded-full transition-colors ${notesEnabled ? "bg-gray-950" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notesEnabled ? "translate-x-4" : ""}`} />
            </button>
            Notes
          </label>
          <Button variant="outline" onClick={onSynthesize} disabled={synth || locked}
            data-testid="synthesize-summary-button" className="rounded-none border-gray-900">
            {synth ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} />}
            {plan.executive_summary ? "Re-synthesize Summary" : "Generate Summary"}
          </Button>
          <Button variant="outline" onClick={onCopyMarkdown} data-testid="copy-markdown-button"
            className="rounded-none border-gray-900">
            <Copy size={16} /> Copy Markdown
          </Button>
          <div className="relative" ref={recentRef}>
            <Button variant="outline" onClick={() => { setShowRecent((v) => !v); loadRecent(); }}
              data-testid="recent-exports-button" className="rounded-none border-gray-900">
              <ClockCounterClockwise size={16} /> Recent
            </Button>
            {showRecent && (
              <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-300 shadow-xl z-30 max-h-80 overflow-auto"
                data-testid="recent-exports-panel">
                <div className="px-3 py-2 border-b border-gray-200 text-[11px] uppercase tracking-wider font-bold text-gray-500">
                  Recent exports
                </div>
                {recent.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-400">No exports yet. Generate a PDF or DOCX.</p>
                ) : (
                  recent.map((r) => (
                    <a key={r.export_id} href={`${BACKEND_URL}${r.download_url}`} target="_blank" rel="noopener noreferrer"
                      onClick={() => setShowRecent(false)}
                      data-testid={`recent-export-${r.export_id}`}
                      className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 text-sm">
                      {r.format === "pdf" ? <FilePdf size={16} className="text-red-600 shrink-0" /> : <FileDoc size={16} className="text-blue-700 shrink-0" />}
                      <span className="flex-1 truncate text-gray-800">{r.filename}</span>
                      <span className="text-[10px] uppercase font-bold text-gray-400">{r.format}</span>
                      <ArrowSquareOut size={13} className="text-gray-400 shrink-0" />
                    </a>
                  ))
                )}
              </div>
            )}
          </div>
          <Button variant="outline" onClick={onDownloadDocx} disabled={docxBusy || locked}
            data-testid="export-docx-button" className="rounded-none border-gray-900">
            {docxBusy ? <CircleNotch size={16} className="animate-spin" /> : <FileDoc size={16} />}
            Download DOCX
          </Button>
          <Button onClick={onDownloadPdf} disabled={pdfBusy || locked} data-testid="export-pdf-button"
            className="rounded-none bg-gray-950 hover:bg-gray-700">
            {pdfBusy ? <CircleNotch size={16} className="animate-spin" /> : <FilePdf size={16} />}
            Download PDF
          </Button>
        </div>
      </div>

      {locked && (
        <div className="no-print px-6 py-3 border-b border-gray-200 bg-white" data-testid="export-lock-strip">
          <LockNotice reason={lockReason} />
        </div>
      )}

      {/* ready bar — direct, user-clickable links. "Save" downloads the file; "open in
          new tab" loads the real file URL in a fresh top-level tab (most reliable inside
          the preview iframe, where blob/auto downloads can be blocked). */}
      {(pdfLink || docxLink) && (
        <div className="no-print sticky top-[57px] z-10 bg-emerald-50 border-b border-emerald-200 px-6 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm" data-testid="export-ready-bar">
          <span className="font-semibold text-emerald-900">Your file is ready.</span>
          {pdfLink && (
            <span className="inline-flex items-center gap-3">
              <a href={pdfLink.href} download={pdfLink.filename} data-testid="save-pdf-link"
                className="inline-flex items-center gap-1.5 font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950">
                <DownloadSimple size={15} /> Save PDF
              </a>
              <a href={pdfLink.url} target="_blank" rel="noopener noreferrer" data-testid="open-pdf-link"
                className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-950 text-xs">
                open in new tab <ArrowSquareOut size={12} />
              </a>
            </span>
          )}
          {docxLink && (
            <span className="inline-flex items-center gap-3">
              <a href={docxLink.href} download={docxLink.filename} data-testid="save-docx-link"
                className="inline-flex items-center gap-1.5 font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950">
                <DownloadSimple size={15} /> Save DOCX
              </a>
              <a href={docxLink.url} target="_blank" rel="noopener noreferrer" data-testid="open-docx-link"
                className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-950 text-xs">
                open in new tab <ArrowSquareOut size={12} />
              </a>
            </span>
          )}
          <span className="text-emerald-700/70 text-xs">If a link does nothing, open this app in its own browser tab and try again.</span>
          <button onClick={onDismissReady} data-testid="close-ready-bar"
            aria-label="Dismiss" className="ml-auto text-emerald-700 hover:text-emerald-950 p-1">
            <X size={16} weight="bold" />
          </button>
        </div>
      )}
    </>
  );
}
